import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Workspace, GeneratedPost, SocialNetwork, PlatformPostCounts,
  PreviewFormat, ReviewCapability, SOCIAL_NETWORKS,
} from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { stampLogoOnPost } from '../utils/stampLogo';
import { autoFillGreenboxForPost, autoAddLogoElement } from '../utils/autoGreenSlot';
import type { PhotoSlot } from '@agency-smm/shared';
import { GenerateScene } from '../components/generatePage/GenerateScene';
import { reviewApi } from '../services/reviewService';
import { useAuth } from '../hooks/useAuth';
import {
  useImageSourceConfig, useTemplateAndThumbnails,
  useAiImagePoller, usePostSelection, usePostGenerationStream,
  useMasonryLayout, usePostActions, useGenerationTriggers, useBulkTemplateActions,
  useOpenPostEditor,
} from '../hooks/generatePage';
import { Button } from '../components/Button';
import { NovahGenerateChat, UserPlan } from '../components/NovahGenerateChat';
import { Toast } from '../components/Toast';
import { PageSkeleton } from '../components/PageSkeleton';
import {
  PostsGrid, GeneratingOverlay, Spinner, GeneratingText,
  SkeletonCard, SkeletonImage, SkeletonLine, SkeletonContent,
  GenerateRoot, ParticleBackdrop, GenerateContent, GenerateBody,
} from '../components/generatePage/styled';
import {
  StudioRoot, Canvas, CanvasHeader, CanvasTitle, CanvasSub, HeaderSpacer,
  Toolbar, FilterRow, FilterChip, FilterCount, SearchBox, SearchField,
  SearchGlyphSlot, SearchClearBtn, NoMatches, Rail, RailHead, RailAvatar,
  RailTitle, RailScroll, RailProgress, ProgressLabel, ProgressTrack,
  ProgressFill,
} from '../components/generatePage/studioStyled';
import { ParticleField } from '../components/ParticleField';
import { PostCard } from '../components/generatePage/PostCard';
import { MasonryGrid } from '../components/generatePage/MasonryGrid';
import { GenerateEmptyState } from '../components/generatePage/GenerateEmptyState';
import { RegenerateModal } from '../components/generatePage/RegenerateModal';
import { PickAndCycleModal } from '../components/generatePage/PickAndCycleModal';
import { BulkSelectionBar } from '../components/generatePage/BulkSelectionBar';
import { LegacyPlatformControls } from '../components/generatePage/LegacyPlatformControls';
import { PREVIEW_FORMATS } from '@agency-smm/shared';

export function GeneratePage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, updateCredits } = useAuth();
  // Solo admins/mods eligen el estilo de imagen en Novah; al resto se le
  // auto-asigna server-side (estilo fijo de Generation Settings) — salvo que el
  // admin haya puesto la política en "They choose", que devuelve la elección.
  const isStaff = ['mod', 'admin', 'super_admin'].includes(user?.role ?? '');
  const [normalUserStyleChoose, setNormalUserStyleChoose] = useState(false);
  const canPickStyle = isStaff || normalUserStyleChoose;
  // Los usuarios normales consultan la política: si es 'choose' pueden elegir el
  // estilo en Novah igual que el staff. Staff no necesita consultarla.
  useEffect(() => {
    if (isStaff) return;
    apiClient.get<{ normalUserStyle: string }>('/api/me/generation-policy')
      .then((p) => setNormalUserStyleChoose(p.normalUserStyle === 'choose'))
      .catch(() => {});
  }, [isStaff]);

  // ─── State ────────────────────────────────────────────────────────────────
  const [quickGenPending, setQuickGenPending] = useState(false);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  // Fijo en 'business' — todos los users son premium por default. El endpoint
  // Stripe legacy /api/billing/subscription (borrado 2026-07-06) ya devolvía
  // 'business' a todos, así que esto es funcionalmente idéntico.
  const userPlan: UserPlan = 'business';
  const [posts, setPosts] = useState<GeneratedPost[]>([]);
  const [platformCounts, setPlatformCounts] = useState<PlatformPostCounts>({
    instagram: 2,
    facebook: 2,
  });
  const [customPrompt, setCustomPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAiFallbackToast, setShowAiFallbackToast] = useState(false);
  const [localError, setLocalError] = useState('');
  // Resumen de la última tanda ("N OK · M fallaron"). Se arma al terminar el
  // stream si hubo al menos un fallo; se descarta al cerrarlo.
  const [genSummary, setGenSummary] = useState<{ ok: number; failed: number } | null>(null);
  // Cuántos posts produjo la ÚLTIMA tanda. El encabezado mostraba el total del
  // workspace (todo lo generado desde siempre) justo después de generar, así que
  // se leía como "esta tanda hizo N": el cliente pidió 10, vio "44 posts" —su
  // biblioteca acumulada desde agosto— y concluyó que la app generaba de más y
  // que por eso su consumo era alto. Ninguna de las dos cosas era cierta.
  const [lastRunCount, setLastRunCount] = useState<number | null>(null);

  // Round-robin picker
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedTemplateIds, setPickedTemplateIds] = useState<string[]>([]);

  // Formato global para el grid y bulk save (default 'vertical' por compat
  // histórica; Novah override este valor cuando arranca un stream).
  const [previewFormat, setPreviewFormat] = useState<PreviewFormat>('vertical');
  const [reviewCapability, setReviewCapability] = useState<ReviewCapability | null>(null);

  // Filtro por red + búsqueda en el copy. Con tandas de 40-80 posts, encontrar
  // "el de LinkedIn que hablaba de garantías" era scrollear a ojo.
  const [networkFilter, setNetworkFilter] = useState<SocialNetwork | 'all'>('all');
  const [postQuery, setPostQuery] = useState('');


  // ─── Extracted hooks ──────────────────────────────────────────────────────
  const imageSourceConfig = useImageSourceConfig(workspaceId);
  const {
    enabledImageSources, imageMix, backgroundImages, selectedAssetIds,
    customColors, colorPickerValue, setColorPickerValue,
    fetchBackgroundImages, toggleImageSource, updateImageMix,
    toggleAssetSelection, selectAllAssets, deselectAllAssets,
    addCustomColor, removeCustomColor, clearAllColors,
  } = imageSourceConfig;

  const templateState = useTemplateAndThumbnails(workspaceId, posts, workspace, setPosts);
  const {
    templates, bulkTemplateId, bulkTemplate, postTemplateOverrides,
    postFormatOverrides,
    compositeThumbnails, setCompositeThumbnails, legibilityWarnings,
    workspaceLogoUrl, brandPhoneNumber, brandContact, buildBrandRenderOptions,
    isBulkProcessing, bulkProgress, templateError,
    fetchTemplates, fetchWorkspaceLogo, fetchBrandInfo,
    refreshWorkspaceOverrides,
    getTemplateForPost,
    handlePostTemplateChange, handleBulkApplyAndDownload,
    handleDownloadSelected, handleSaveSelectedToGallery,
    handlePostFormatChange,
  } = templateState;

  const {
    selectedPostIds, setSelectedPostIds, togglePost, clearSelection,
  } = usePostSelection();

  const stream = usePostGenerationStream({
    workspaceId: workspaceId!,
    bulkTemplate,
    workspace,
    workspaceLogoUrl,
    brandPhoneNumber,
    getTemplateForPost,
    buildBrandRenderOptions,
    setPosts,
    setCompositeThumbnails,
    updateCredits,
  });
  const {
    isStreaming, streamingPosts, skeletonCount, streamProgress,
    error: streamError, setError: setStreamError,
    errors: streamErrors, succeededCount,
    startStream,
  } = stream;
  const error = localError || streamError;
  const setError = useCallback((msg: string) => {
    setLocalError(msg);
    setStreamError(msg);
  }, [setStreamError]);

  useAiImagePoller(posts, workspaceId, setPosts);

  // Al terminar una tanda (isStreaming: true → false), si hubo errores armamos
  // el resumen "N OK · M fallaron". wasStreamingRef detecta el flanco de bajada.
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      setLastRunCount(succeededCount);
      if (streamErrors.length > 0) {
        setGenSummary({ ok: succeededCount, failed: streamErrors.length });
      }
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, streamErrors.length, succeededCount]);

  // Acciones por-post (like/dislike/variaciones/retry/delete/regenerate) + su
  // estado efímero, extraídas a un hook.
  const {
    likedIds, actionBusy, regenPostId, setRegenPostId, isRegenerating,
    handleDeletePost, handleRegenerate, handleLikePost, handleDislikePost,
    handleMakeVariations, handleRetryImage, handleRetryPending,
  } = usePostActions({
    workspaceId, setPosts, setError, previewFormat, handleSaveSelectedToGallery,
  });

  // Escena de generación a pantalla completa (sin modal): se muestra en el
  // arranque del stream (antes de que lleguen los primeros posts). `sceneMounted`
  // sobrevive hasta que termina la animación de salida (cross-fade al grid).
  const showScene = isStreaming && streamingPosts.length === 0;
  const [sceneMounted, setSceneMounted] = useState(false);
  useEffect(() => { if (showScene) setSceneMounted(true); }, [showScene]);

  // Layout masonry para el grid de streaming (mientras se generan). El grid
  // final usa <MasonryGrid> (que ya trae su propio layout + drag para reordenar).
  const streamGridRef = useRef<HTMLDivElement>(null);
  useMasonryLayout(streamGridRef, [streamingPosts.length, skeletonCount]);

  // Reorden por drag — solo en sesión (no persiste en DB). Mueve el array local
  // de posts; el poller y los thumbnails mapean por id, así que el orden aguanta.
  const handleReorderPosts = useCallback((next: GeneratedPost[]) => {
    setPosts(next);
  }, []);

  // Intención de estampar el logo en los ads baked-ai de la tanda actual.
  // Default OFF: la IA genera el poster LIMPIO y el logo es OPCIONAL — se agrega
  // o bien en la generación (checkbox "Include my logo" de Novah) o después en el
  // editor (PreviewModal: stamp/move/remove, no-destructivo). Antes se estampaba
  // siempre; ahora respeta la elección del usuario.
  const [autoStampLogo, setAutoStampLogo] = useState(false);
  const [autoFillGreenbox, setAutoFillGreenbox] = useState(false);

  // Orquestación post-generación de los ads con estilo (baked-ai) de ESTA sesión,
  // SOLO lo que el usuario pidió con los checkboxes de Novah. Orden garantizado:
  //   1) auto-fill del hueco verde (la IA elige la foto de la marca y la mete) —
  //      no-op si el estilo no es greenbox o no hay foto que pegue;
  //   2) el logo SIEMPRE arriba (sobre la foto ya puesta).
  // Solo posts que estuvieron "pending" (los nuevos), no los viejos de la DB.
  // Cada paso es no-destructivo y reposicionable/reemplazable desde el editor.
  const wasPendingRef = useRef<Set<string>>(new Set());
  const orchestratedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!workspaceId) return;
    const wantLogo = autoStampLogo && !!workspaceLogoUrl;
    if (!autoFillGreenbox && !wantLogo) return;
    for (const post of posts) {
      if (post.imageSource !== 'baked-ai') continue;
      if (post.pendingImageTaskId) { wasPendingRef.current.add(post.id); continue; }
      if (!post.imageUrl || post.originalImageUrl) continue;
      if (!wasPendingRef.current.has(post.id) || orchestratedRef.current.has(post.id)) continue;
      orchestratedRef.current.add(post.id);
      const target = post;
      void (async () => {
        try {
          let baseUrl = target.imageUrl!;
          let slot: PhotoSlot | null = null;
          // 1) Foto en el hueco verde (detecta el verde; si no hay, no toca nada).
          if (autoFillGreenbox) {
            const filled = await autoFillGreenboxForPost(workspaceId, target);
            if (filled) {
              baseUrl = filled.snapshotUrl; slot = filled.slot;
              setPosts((prev) => prev.map((p) => (p.id === target.id ? { ...p, imageUrl: filled.snapshotUrl, photoSlot: filled.slot } : p)));
              setCompositeThumbnails((prev) => ({ ...prev, [target.id]: filled.snapshotUrl }));
            }
          }
          // 2) Logo arriba. En greenbox (hay foto/capa) va como ELEMENTO editable
          //    (seleccionable/movible en el editor) + snapshot regenerado. En el
          //    resto de los baked-ai, estampado plano como antes.
          if (wantLogo) {
            if (slot) {
              const res = await autoAddLogoElement(workspaceId, target.id, slot, workspaceLogoUrl!);
              if (res) {
                setPosts((prev) => prev.map((p) => (p.id === target.id ? { ...p, imageUrl: res.imageUrl, extraElements: res.elements } : p)));
                setCompositeThumbnails((prev) => ({ ...prev, [target.id]: res.imageUrl }));
              }
            } else {
              const newUrl = await stampLogoOnPost(workspaceId, target.id, baseUrl, workspaceLogoUrl!);
              if (newUrl) {
                setPosts((prev) => prev.map((p) => (p.id === target.id ? { ...p, imageUrl: newUrl, originalImageUrl: baseUrl } : p)));
                setCompositeThumbnails((prev) => ({ ...prev, [target.id]: newUrl }));
              }
            }
          }
        } catch {
          orchestratedRef.current.delete(target.id);
        }
      })();
    }
  }, [posts, workspaceLogoUrl, workspaceId, autoStampLogo, autoFillGreenbox, setPosts, setCompositeThumbnails]);

  // ─── Initial fetches ──────────────────────────────────────────────────────
  useEffect(() => {
    if (searchParams.get('quickgen') === 'true') {
      setQuickGenPending(true);
      setSearchParams({}, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (workspaceId) {
      fetchWorkspace();
      fetchPosts();
      fetchTemplates();
      fetchBackgroundImages();
      fetchWorkspaceLogo();
      fetchBrandInfo();
      refreshWorkspaceOverrides();
    }
    reviewApi.getReviewCapability()
      .then(setReviewCapability)
      .catch(() => setReviewCapability(null));
  }, [workspaceId]);

  const fetchWorkspace = async () => {
    try {
      const data = await apiClient.get<Workspace>(`/api/workspaces/${workspaceId}`);
      setWorkspace(data);
    } catch (err) {
      console.error('Failed to fetch workspace:', err);
    }
  };

  const fetchPosts = async () => {
    try {
      const data = await apiClient.get<GeneratedPost[]>(`/api/workspaces/${workspaceId}/posts`);
      setPosts(data);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    }
  };

  // ─── Platform handlers ────────────────────────────────────────────────────
  const togglePlatform = useCallback((platform: SocialNetwork) => {
    setPlatformCounts((prev) => {
      if (prev[platform] !== undefined) {
        const next = { ...prev };
        delete next[platform];
        return next;
      }
      return { ...prev, [platform]: 2 };
    });
  }, []);

  const updatePlatformCount = useCallback((platform: SocialNetwork, count: number) => {
    setPlatformCounts((prev) => ({
      ...prev,
      [platform]: Math.max(0, Math.min(20, count)),
    }));
  }, []);

  const totalPostCount = useMemo(() => {
    return Object.values(platformCounts).reduce((sum, count) => sum + (count || 0), 0);
  }, [platformCounts]);

  // ─── Generation triggers ──────────────────────────────────────────────────
  // Disparadores de generación (legacy panel + Novah), extraídos a un hook.
  const { handleLegacyStream, handleNovahGenerate } = useGenerationTriggers({
    user, userPlan, totalPostCount, platformCounts, customPrompt, previewFormat,
    enabledImageSources, imageMix, customColors, selectedAssetIds, backgroundImages,
    startStream, setError, setPlatformCounts, setCustomPrompt, setPreviewFormat, setAutoStampLogo,
    setAutoFillGreenbox,
  });

  // Anti doble-submit (cliente): mientras hay una generación en vuelo
  // (isStreaming/isGenerating) ignoramos nuevos disparos. Mitigación principal —
  // la idempotencia server-side queda como follow-up. Envolvemos ambos triggers.
  const generationInFlight = isStreaming || isGenerating;
  const guardedNovahGenerate = useCallback(
    (config: Parameters<typeof handleNovahGenerate>[0]) => {
      if (generationInFlight) return;
      void handleNovahGenerate(config);
    },
    [generationInFlight, handleNovahGenerate],
  );
  const guardedLegacyStream = useCallback(() => {
    if (generationInFlight) return;
    handleLegacyStream();
  }, [generationInFlight, handleLegacyStream]);

  // Quick-gen auto-trigger después de cargar workspace.
  useEffect(() => {
    if (quickGenPending && workspace && !isStreaming && !isGenerating
        && totalPostCount > 0 && user && user.credits >= totalPostCount) {
      setQuickGenPending(false);
      handleLegacyStream();
    }
  }, [quickGenPending, workspace, isStreaming, isGenerating, totalPostCount, user, handleLegacyStream]);

  // ─── Post-level handlers ──────────────────────────────────────────────────
  const openPostEditor = useOpenPostEditor({
    workspaceId, navigate, posts, templates, workspace, previewFormat,
    postTemplateOverrides, postFormatOverrides, bulkTemplate, bulkTemplateId,
    getTemplateForPost, workspaceLogoUrl, brandPhoneNumber, brandContact,
    reviewCapability, brandColors: templateState.brandColors, brandFonts: templateState.brandFonts,
  });

  // @ts-expect-error handleDeletePost will be wired to PreviewModal in a future iteration
  globalThis.__handleDeletePost = handleDeletePost;

  // ─── Bulk-bar handlers ────────────────────────────────────────────────────
  const {
    handleBulkApplyTemplate, handleRandomize, handlePickerApply,
    handlePreviewFormatChange, handleSaveToGallery, handleBulkDelete,
  } = useBulkTemplateActions({
    selectedPostIds, clearSelection, templates, previewFormat, setPreviewFormat,
    handlePostTemplateChange, handlePostFormatChange, handleSaveSelectedToGallery, handleDeletePost,
  });

  // Silenciar unused: isGenerating queda como state local para futuras
  // integraciones (panel legacy lo respeta como disabled-state).
  void isGenerating;
  void setIsGenerating;
  void localError; // se consume vía `error`
  void templateError; // se reporta en LegacyPlatformControls

  // ─── Filtro de la grilla ──────────────────────────────────────────────────
  // Solo las redes que realmente tienen posts: mostrar 11 chips con 9 en cero
  // es ruido.
  const networkTabs = useMemo(() => {
    const counts = new Map<SocialNetwork, number>();
    for (const p of posts) counts.set(p.network, (counts.get(p.network) || 0) + 1);
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([network, count]) => ({ network, count }));
  }, [posts]);

  const isFiltering = networkFilter !== 'all' || postQuery.trim().length > 0;

  const visiblePosts = useMemo(() => {
    const q = postQuery.trim().toLowerCase();
    return posts.filter((p) => {
      if (networkFilter !== 'all' && p.network !== networkFilter) return false;
      if (q && !(p.content || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [posts, networkFilter, postQuery]);

  // Si la red filtrada se queda sin posts (borrado masivo), volvemos a "All"
  // en vez de dejar la pantalla vacía con un chip que ya no existe.
  useEffect(() => {
    if (networkFilter === 'all') return;
    if (!posts.some((p) => p.network === networkFilter)) setNetworkFilter('all');
  }, [posts, networkFilter]);

  if (!workspace) {
    return <PageSkeleton />;
  }

  return (
    <GenerateRoot>
      <ParticleBackdrop aria-hidden>
        <ParticleField density={0.6} />
      </ParticleBackdrop>
      <GenerateContent>
      {/* Escena de generación a pantalla completa (in-place, sin modal): el
          cuerpo retrocede (GenerateBody $receded) y la escena entra por encima.
          Al llegar los primeros posts hace cross-fade al grid de streaming. */}
      {sceneMounted && (
        <GenerateScene
          active={showScene}
          onExited={() => setSceneMounted(false)}
          title="Generating your content…"
          subtitle="Writing each post and composing its visual. AI images can take up to ~60s — don't close this tab."
          steps={['Planning your content', 'Writing captions', 'Choosing visuals', 'Composing posts']}
        />
      )}

      <GenerateBody $receded={showScene}>
      <StudioRoot>
        {/* ── Canvas: solo el trabajo ── */}
        <Canvas>
          <CanvasHeader>
            <div>
              <CanvasTitle>Generate Content</CanvasTitle>
              <CanvasSub>
                {posts.length === 0
                  ? `Create AI-powered posts for ${workspace.name}`
                  : lastRunCount !== null
                    // Lo que salió de ESTA tanda va primero y separado del total
                    // acumulado: son dos números distintos y confundirlos hace
                    // creer que se generó (y se pagó) de más.
                    ? `${lastRunCount} just generated · ${posts.length} in this workspace`
                    : `${posts.length} post${posts.length !== 1 ? 's' : ''} in this workspace · all time`}
              </CanvasSub>
            </div>
            <HeaderSpacer>
              <Button $variant="outline" onClick={() => navigate(`/workspace/${workspaceId}`)}>
                Back to Workspace
              </Button>
            </HeaderSpacer>
          </CanvasHeader>

          {/* Filtros + búsqueda: solo tienen sentido con una tanda ya hecha y
              cuando no estamos en medio de un stream. */}
          {!isGenerating && !isStreaming && streamingPosts.length === 0 && posts.length > 0 && (
            <Toolbar>
              <FilterRow>
                <FilterChip
                  type="button"
                  $active={networkFilter === 'all'}
                  onClick={() => setNetworkFilter('all')}
                >
                  All
                  <FilterCount $active={networkFilter === 'all'}>{posts.length}</FilterCount>
                </FilterChip>
                {networkTabs.map(({ network, count }) => (
                  <FilterChip
                    key={network}
                    type="button"
                    $active={networkFilter === network}
                    onClick={() => setNetworkFilter(network)}
                  >
                    {SOCIAL_NETWORKS[network]?.label || network}
                    <FilterCount $active={networkFilter === network}>{count}</FilterCount>
                  </FilterChip>
                ))}
              </FilterRow>

              <SearchBox>
                <SearchGlyphSlot>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="7" cy="7" r="4.75" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </SearchGlyphSlot>
                <SearchField
                  type="search"
                  value={postQuery}
                  onChange={(e) => setPostQuery(e.target.value)}
                  placeholder="Search copy"
                  aria-label="Search post copy"
                />
                {postQuery && (
                  <SearchClearBtn type="button" onClick={() => setPostQuery('')} aria-label="Clear search">
                    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    </svg>
                  </SearchClearBtn>
                )}
              </SearchBox>
            </Toolbar>
          )}

          {isGenerating ? (
            <GeneratingOverlay>
              <Spinner />
              <GeneratingText>
                Generating {totalPostCount} posts...
                <br />
                AI images can take up to ~60s. Don&apos;t close this tab.
              </GeneratingText>
            </GeneratingOverlay>
          ) : isStreaming || streamingPosts.length > 0 ? (
            /* El progreso ya no va acá: vive en el rail, en el mismo lugar
               siempre, mientras el canvas se llena de posts. */
            <PostsGrid ref={streamGridRef}>
              {streamingPosts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  effectiveFormat={postFormatOverrides[post.id] || previewFormat}
                  compositeThumbnail={compositeThumbnails[post.id]}
                  illegible={legibilityWarnings[post.id]}
                  onOpen={openPostEditor}
                  onLike={handleLikePost}
                  onDislike={handleDislikePost}
                  onMakeVariations={handleMakeVariations}
                  onRetry={handleRetryImage}
                  onRetryPending={handleRetryPending}
                  liked={likedIds.has(post.id)}
                  actionBusy={actionBusy}
                />
              ))}
              {Array.from({ length: skeletonCount }).map((_, i) => (
                <SkeletonCard key={`skeleton-${i}`}>
                  <SkeletonImage $aspectRatio={PREVIEW_FORMATS[previewFormat].aspectRatio} />
                  <SkeletonContent>
                    <SkeletonLine $width="80%" />
                    <SkeletonLine $width="100%" />
                    <SkeletonLine $width="60%" />
                  </SkeletonContent>
                </SkeletonCard>
              ))}
            </PostsGrid>
          ) : posts.length === 0 ? (
            <GenerateEmptyState workspaceId={workspaceId} />
          ) : (
            <>
              <BulkSelectionBar
                selectedPostIds={selectedPostIds}
                totalPosts={posts.length}
                templates={templates}
                isBulkProcessing={isBulkProcessing}
                bulkProgress={bulkProgress}
                previewFormat={previewFormat}
                pickedTemplateIds={pickedTemplateIds}
                onApplyTemplate={handleBulkApplyTemplate}
                onRandomize={handleRandomize}
                onOpenPicker={() => setPickerOpen(true)}
                onSelectAll={() => setSelectedPostIds(new Set(visiblePosts.map(p => p.id)))}
                onClearSelection={clearSelection}
                onDownload={(quality) => handleDownloadSelected(selectedPostIds, quality)}
                onDownloadCSV={(quality) => handleDownloadSelected(selectedPostIds, quality, 'csv')}
                onPreviewFormatChange={handlePreviewFormatChange}
                onSaveToGallery={handleSaveToGallery}
                onBulkDelete={handleBulkDelete}
              />
              {visiblePosts.length === 0 ? (
                <NoMatches>No posts match this filter.</NoMatches>
              ) : (
                <MasonryGrid
                  items={visiblePosts}
                  getKey={(post) => post.id}
                  /* Sin onReorder cuando hay filtro: handleReorderPosts hace
                     setPosts(next) con lo que recibe, así que reordenar una
                     vista filtrada BORRARÍA los posts ocultos. */
                  onReorder={isFiltering ? undefined : handleReorderPosts}
                  layoutDeps={[compositeThumbnails, visiblePosts.length]}
                  renderItem={(post) => (
                    <PostCard
                      post={post}
                      effectiveFormat={postFormatOverrides[post.id] || previewFormat}
                      compositeThumbnail={compositeThumbnails[post.id]}
                      illegible={legibilityWarnings[post.id]}
                      isSelected={selectedPostIds.has(post.id)}
                      onSelectToggle={togglePost}
                      onOpen={openPostEditor}
                      onLike={handleLikePost}
                      onDislike={handleDislikePost}
                      onMakeVariations={handleMakeVariations}
                      onRetry={handleRetryImage}
                      onRetryPending={handleRetryPending}
                      liked={likedIds.has(post.id)}
                      actionBusy={actionBusy}
                    />
                  )}
                />
              )}
            </>
          )}
        </Canvas>

        {/* ── Rail: Novah + progreso + ajustes. Siempre presente. ── */}
        <Rail>
          <RailHead>
            <RailAvatar>
              <img src="/novah_laugh.png" alt="" />
            </RailAvatar>
            <RailTitle>
              <strong>Novah</strong>
              <span>{isStreaming ? 'Generating…' : `Content co-pilot · ${workspace.name}`}</span>
            </RailTitle>
          </RailHead>

          <RailScroll>
            <NovahGenerateChat
              workspaceId={workspaceId!}
              workspaceName={workspace.name}
              credits={user?.credits ?? 0}
              hasBackgroundImages={backgroundImages.length > 0}
              userPlan={userPlan}
              canPickStyle={canPickStyle}
              onNavigateBilling={() => navigate('/subscription')}
              onGenerate={guardedNovahGenerate}
              isStreaming={isStreaming}
              streamProgress={streamProgress}
              onExportCSV={handleBulkApplyAndDownload}
              onDownloadZIP={handleBulkApplyAndDownload}
              postsCount={posts.length + streamingPosts.length}
            />

            {/* Los controles legacy se auto-ocultan (display:none hardcodeado en
                su raíz): quedan montados para no cambiar el cableado de estado,
                pero NO se exponen. Reactivarlos es decisión de producto. */}
            <LegacyPlatformControls
                  platformCounts={platformCounts}
                  totalPostCount={totalPostCount}
                  togglePlatform={togglePlatform}
                  updatePlatformCount={updatePlatformCount}
                  enabledImageSources={enabledImageSources}
                  imageMix={imageMix}
                  toggleImageSource={toggleImageSource}
                  updateImageMix={updateImageMix}
                  customColors={customColors}
                  colorPickerValue={colorPickerValue}
                  setColorPickerValue={setColorPickerValue}
                  addCustomColor={addCustomColor}
                  removeCustomColor={removeCustomColor}
                  clearAllColors={clearAllColors}
                  backgroundImages={backgroundImages}
                  selectedAssetIds={selectedAssetIds}
                  toggleAssetSelection={toggleAssetSelection}
                  selectAllAssets={selectAllAssets}
                  deselectAllAssets={deselectAllAssets}
                  customPrompt={customPrompt}
                  setCustomPrompt={setCustomPrompt}
                  error={error}
                  templateError={templateError}
                  isGenerating={isGenerating}
                  isStreaming={isStreaming}
                  userCredits={user?.credits ?? 0}
                  onGenerate={guardedLegacyStream}
                />
          </RailScroll>

          {isStreaming && streamProgress.total > 0 && (
            <RailProgress>
              <ProgressLabel>
                <span>Generating… AI images take up to 60s</span>
                <span>{streamProgress.current}/{streamProgress.total}</span>
              </ProgressLabel>
              <ProgressTrack>
                <ProgressFill $progress={(streamProgress.current / streamProgress.total) * 100} />
              </ProgressTrack>
            </RailProgress>
          )}

        </Rail>
      </StudioRoot>
      </GenerateBody>

      {/* El editor de posts vive en /workspace/:id/posts/:postId/edit — ver
          PreviewEditorPage. Llegás ahí desde openPostEditor(post). */}

      <RegenerateModal
        open={!!regenPostId}
        isRegenerating={isRegenerating}
        onRegenerate={handleRegenerate}
        onClose={() => setRegenPostId(null)}
      />

      <PickAndCycleModal
        open={pickerOpen}
        templates={templates}
        selectedPostIds={selectedPostIds}
        pickedTemplateIds={pickedTemplateIds}
        setPickedTemplateIds={setPickedTemplateIds}
        onApply={handlePickerApply}
        onClose={() => setPickerOpen(false)}
      />

      {showAiFallbackToast && (
        <Toast
          title="AI GENERATION LIMIT"
          message="Some images couldn't be generated with AI. Using stock photos instead."
          variant="warning"
          duration={5000}
          onClose={() => setShowAiFallbackToast(false)}
        />
      )}

      {genSummary && (
        <Toast
          title="Generation finished"
          message={`${genSummary.ok} OK · ${genSummary.failed} failed`}
          variant="warning"
          duration={6000}
          onClose={() => setGenSummary(null)}
        />
      )}
      </GenerateContent>
    </GenerateRoot>
  );
}
